import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

const STATUS_BADGE = {
  trialing:   { label: 'Free Trial',      className: 'bg-blue-100 text-blue-700 border-blue-200' },
  active:     { label: 'Active',          className: 'bg-green-100 text-green-700 border-green-200' },
  past_due:   { label: 'Payment Issue',   className: 'bg-amber-100 text-amber-700 border-amber-200' },
  cancelled:  { label: 'Cancelled',       className: 'bg-red-100 text-red-700 border-red-200' },
  canceled:   { label: 'Cancelled',       className: 'bg-red-100 text-red-700 border-red-200' },
  unpaid:     { label: 'Payment Failed',  className: 'bg-red-100 text-red-700 border-red-200' },
  incomplete: { label: 'Incomplete Setup',className: 'bg-orange-100 text-orange-700 border-orange-200' },
  none:       { label: 'No Subscription', className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const SubscriptionStatusCard = ({ company: companyProp, siteUsage, isDemoAdmin }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);

  // okzdbyNN: single-source the company row from the prop the dashboard
  // already loaded. The previous prop-then-fresh swap (an independent
  // companies refetch) raced loadDashboardData and made the limit flicker.

  // Billing UI is admin-only — site managers, workers, and clients must not see
  // subscription status or any "Manage Subscription" affordance.
  if (userProfile?.role !== 'admin') return null;
  if (!companyProp || isDemoAdmin) return null;

  const company = companyProp;

  const rawStatus = company?.subscription_status;
  const trialEnd = company?.trial_end_date
    ? new Date(company.trial_end_date)
    : null;
  const trialStart = company?.trial_start_date ? new Date(company.trial_start_date) : null;
  const now = new Date();
  const hasStripeSubscription = !!company?.stripe_subscription_id;

  // ── Effective status resolution ──────────────────────────────────────────
  let subscriptionStatus = rawStatus || 'inactive';

  // If Stripe says trialing, trust it regardless of local trial dates
  // If status is 'inactive' but trial dates exist and trial hasn't ended → trialing
  if (
    (subscriptionStatus === 'inactive' || !rawStatus) &&
    trialStart &&
    trialEnd &&
    trialEnd > now
  ) {
    subscriptionStatus = 'trialing';
  }

  // If a Stripe subscription exists but status is still 'inactive', treat as active
  // (handles edge case where webhook hasn't fired yet or status wasn't updated)
  if (subscriptionStatus === 'inactive' && hasStripeSubscription) {
    subscriptionStatus = 'active';
  }

  // If status is still 'inactive' and there's no Stripe subscription at all → no subscription
  const hasNoSubscription =
    (subscriptionStatus === 'inactive') &&
    !hasStripeSubscription &&
    !trialStart &&
    !trialEnd;

  const statusBadge = hasNoSubscription
    ? STATUS_BADGE?.none
    : STATUS_BADGE?.[subscriptionStatus] || {
        label: subscriptionStatus,
        className: 'bg-gray-100 text-gray-700 border-gray-200',
      };

  const currentPeriodEnd = company?.current_period_end
    ? new Date(company.current_period_end)
    : null;

  const formatDate = (date) =>
    date?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const isTrialing  = subscriptionStatus === 'trialing';
  const isActive    = subscriptionStatus === 'active';
  const isPastDue   = subscriptionStatus === 'past_due';
  const isCancelled =
    subscriptionStatus === 'cancelled' ||
    subscriptionStatus === 'canceled' ||
    subscriptionStatus === 'unpaid';

  // For trialing: use trial_end_date first, fall back to current_period_end (Stripe sets them equal)
  const effectiveTrialEnd = trialEnd || (isTrialing ? currentPeriodEnd : null);

  // Billing date: trial → trial end date; active → current_period_end
  const billingDate = isTrialing
    ? formatDate(effectiveTrialEnd)
    : isActive
    ? formatDate(currentPeriodEnd)
    : null;

  // Access-until date for cancelled subscriptions
  const accessUntilDate = isCancelled && currentPeriodEnd
    ? formatDate(currentPeriodEnd)
    : null;

  const activeSites = siteUsage?.active_count ?? '—';
  const maxSites    = company?.max_active_sites ?? siteUsage?.max_sites ?? '—';

  const billingInterval = company?.billing_interval
    ? company?.billing_interval?.charAt(0)?.toUpperCase() + company?.billing_interval?.slice(1)
    : null;

  const handleManageSubscription = async () => {
    if (!company?.id) {
      navigate('/subscription-management');
      return;
    }
    if (company?.stripe_customer_id) {
      setPortalLoading(true);
      try {
        const { data, error } = await stripeService?.createPortalSession(company?.id);
        if (!error && data?.url) {
          window.location.href = data?.url;
        } else {
          navigate('/subscription-management');
        }
      } catch {
        navigate('/subscription-management');
      } finally {
        setPortalLoading(false);
      }
    } else {
      navigate('/subscription-management');
    }
  };

  // ── No Subscription State ────────────────────────────────────────────────
  if (hasNoSubscription) {
    return (
      <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-5 md:p-6 mb-6 md:mb-8">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Icon name="CreditCard" size={18} className="text-accent" />
            </div>
            <div>
              <h2 className="text-base md:text-lg font-semibold text-foreground font-heading">Subscription</h2>
              <p className="text-xs text-muted-foreground">Your current plan and billing status</p>
            </div>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusBadge?.className}`}>
            {statusBadge?.label}
          </span>
        </div>

        <div className="flex flex-col items-center text-center py-4 px-2 mb-5">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
            <Icon name="CreditCard" size={22} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-semibold text-foreground mb-1">No active subscription</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Start your free trial to unlock all features. No credit card required to get started.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/subscription-management')}
            className="w-full sm:w-auto"
          >
            <Icon name="Zap" size={14} />
            Start Free Trial
          </Button>
        </div>
      </div>
    );
  }

  // ── Standard Subscription States ────────────────────────────────────────
  return (
    <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-5 md:p-6 mb-6 md:mb-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="CreditCard" size={18} className="text-accent" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-semibold text-foreground font-heading">Subscription</h2>
            <p className="text-xs text-muted-foreground">Your current plan and billing status</p>
          </div>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusBadge?.className}`}>
          {statusBadge?.label}
        </span>
      </div>

      {/* Past Due Warning Banner */}
      {isPastDue && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5">
          <Icon name="AlertTriangle" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800 font-medium">
            Payment issue — please update your payment method to avoid losing access.
          </p>
        </div>
      )}

      {/* Cancelled Banner */}
      {isCancelled && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
          <Icon name="Lock" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800 font-medium">
            {accessUntilDate
              ? `Your subscription has been cancelled. You have access until ${accessUntilDate}.`
              : 'Your subscription is no longer active. Please subscribe to continue using SiteProof.'}
          </p>
        </div>
      )}

      {/* Trial Info Banner */}
      {isTrialing && effectiveTrialEnd && (
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-5">
          <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-800 font-medium">
              Your free trial ends on {formatDate(effectiveTrialEnd)}
            </p>
            <p className="text-xs text-blue-700 mt-0.5">
              You will be billed on {formatDate(effectiveTrialEnd)}
            </p>
          </div>
        </div>
      )}

      {/* Details Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        {/* Plan Name */}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Plan</span>
          <span className="text-sm font-semibold text-foreground">{company?.plan_tier === 'per_site' ? 'Per Site' : company?.plan_tier || 'Free Trial'}</span>
        </div>

        {/* Active sites vs plan allowance — explicit copy replaces the old
            ambiguous "X / Y" that Heidi misread as her limit (okzdbyNN). */}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Active sites</span>
          <span className="text-sm font-semibold text-foreground">
            {(maxSites === '—' || maxSites === null)
              ? `${activeSites} active`
              : `${activeSites} of ${maxSites} allowed`}
          </span>
        </div>

        {/* Billing Date — shown for active (non-trialing) only */}
        {isActive && !isTrialing && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">
              Next Billing Date
            </span>
            {billingDate ? (
              <span className="text-sm font-semibold text-foreground">{billingDate}</span>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </div>
        )}

        {/* Billing Interval — shown for active paid subscriptions only */}
        {isActive && billingInterval && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Billing</span>
            <span className="text-sm font-semibold text-foreground">{billingInterval}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant={isCancelled ? 'primary' : 'outline'}
          size="sm"
          onClick={isCancelled ? () => navigate('/subscription-management') : handleManageSubscription}
          disabled={portalLoading}
          className="w-full sm:w-auto"
        >
          {portalLoading ? (
            <>
              <Icon name="Loader2" size={14} className="animate-spin" />
              Opening…
            </>
          ) : isCancelled ? (
            <>
              <Icon name="CreditCard" size={14} />
              Choose Plan
            </>
          ) : isPastDue ? (
            <>
              <Icon name="CreditCard" size={14} />
              Update Payment Method
            </>
          ) : (
            <>
              <Icon name="ExternalLink" size={14} />
              Manage Subscription
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionStatusCard;
